import { uploadImageDataURL } from "@/service/editorService";
import { setEtapaCompra } from "@/service/firebase";
import typeEtapa from "@/constants/etapaCompra";
import {
  analisarElemento,
  analyzeImageDataUrl as analyzeImageDataUrlUtil,
  exportCanvasWithValidation as exportCanvasWithValidationUtil,
  regiaoFicouVazia,
} from "@/utils/analyzerImage";
import {
  PASSAPORTE_LARGURA_CM,
  PASSAPORTE_ALTURA_CM,
} from "@/formats/passaporteFormat";
import { comTimeout } from "@/utils/promiseTimeout";

// Tentativas de montar cada folha. A última refaz a exportação da foto a
// partir do canvas do editor, em vez de reaproveitar o dataUrl da prévia.
const TENTATIVAS_MONTAGEM = 3;
// Tempo máximo de cada tentativa de montagem de uma folha.
const TEMPO_LIMITE_MONTAGEM_MS = 20000;

// Envio de cada folha para o Storage: tentativa e erro por no máximo este
// tempo (o prazo recomeça a cada folha). Passado o prazo, o editor abre o
// modal de erro com "Tentar novamente | Reiniciar".
export const PRAZO_ENVIO_MS = 120000;
// Tempo máximo de cada tentativa de envio (sobra prazo para tentar de novo).
const TEMPO_MAXIMO_TENTATIVA_ENVIO_MS = 40000;
// Espera entre tentativas de envio (a última se repete).
const ESPERAS_ENVIO_MS = [1000, 2000, 4000, 5000];

// Por que a folha não pôde ser montada.
export const FALHAS_FOLHA = {
  // A foto não carregou no canvas da folha.
  MONTAGEM: "montagem",
  // A foto foi desenhada, mas a área dela na folha ficou branca.
  CONFERENCIA: "conferencia",
  // O navegador não conseguiu criar/exportar o canvas da folha.
  MEMORIA: "memoria",
  // A montagem passou do tempo máximo.
  TEMPO_ESGOTADO: "tempo_esgotado",
  // A folha foi montada, mas o envio para o Storage falhou.
  UPLOAD: "upload",
};

// A folha não pôde ser montada: nada é enviado para a pasta `editada`.
export class ErroComposicaoFolha extends Error {
  constructor(mensagem, detalhes = {}) {
    super(mensagem);
    this.name = "ErroComposicaoFolha";
    this.tipo = detalhes.tipo || FALHAS_FOLHA.MONTAGEM;
    this.detalhes = detalhes;
  }
}

// A folha foi montada, mas não chegou ao Storage.
export class ErroEnvioFolha extends Error {
  constructor(mensagem, detalhes = {}) {
    super(mensagem);
    this.name = "ErroEnvioFolha";
    this.tipo = FALHAS_FOLHA.UPLOAD;
    this.detalhes = detalhes;
  }
}

function carregarImagemDaFolha(dataUrl, rotulo) {
  return new Promise((resolve, reject) => {
    const falhar = (causa) =>
      reject(
        new ErroComposicaoFolha(`Foto ${rotulo} não carregou na folha`, {
          tipo: FALHAS_FOLHA.MONTAGEM,
          causa,
        })
      );
    if (!dataUrl) {
      falhar("sem_imagem");
      return;
    }
    const img = new Image();
    img.onload = () => {
      if (!img.width || !img.height) {
        falhar("imagem_sem_dimensoes");
        return;
      }
      resolve(img);
    };
    // Antes o onerror devolvia a folha só com o fundo branco, como se a
    // montagem tivesse dado certo, e ela seguia para a impressão.
    img.onerror = () => falhar("falha_ao_carregar");
    img.src = dataUrl;
  });
}

function criarFolha(largura, altura) {
  const sheetCanvas = document.createElement("canvas");
  sheetCanvas.width = largura;
  sheetCanvas.height = altura;
  const ctx = sheetCanvas.getContext("2d");
  // Sem memória para o canvas o navegador devolve contexto nulo.
  if (!ctx) {
    throw new ErroComposicaoFolha("Canvas da folha não pôde ser criado", {
      tipo: FALHAS_FOLHA.MEMORIA,
      causa: "contexto_nulo",
    });
  }
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, largura, altura);
  return { sheetCanvas, ctx };
}

// Com o canvas sem memória o Chrome devolve "data:," em vez de lançar erro;
// isso viraria um arquivo vazio na pasta `editada`.
function exportarFolha(sheetCanvas) {
  const dataUrl = sheetCanvas.toDataURL("image/png");
  if (!dataUrl || !dataUrl.startsWith("data:image/png")) {
    throw new ErroComposicaoFolha("Folha não pôde ser exportada", {
      tipo: FALHAS_FOLHA.MEMORIA,
      causa: "exportacao_vazia",
    });
  }
  return dataUrl;
}

// Confere, na folha já montada, que a área onde a foto foi desenhada não ficou
// branca lisa enquanto a prévia dessa foto não era.
function conferirRegiao(sheetCanvas, recorte, analiseEsperada, rotulo) {
  const analise = analisarElemento(sheetCanvas, recorte);
  if (regiaoFicouVazia(analise, analiseEsperada)) {
    throw new ErroComposicaoFolha(`Foto ${rotulo} ficou em branco na folha`, {
      tipo: FALHAS_FOLHA.CONFERENCIA,
      causa: "regiao_em_branco",
    });
  }
}

export function useExport({
  canvas,
  formato,
  changedOrientationFn,
  typeIsOrientationFn,
  onExportCompleted = async () => {},
  tentativasMontagem = TENTATIVAS_MONTAGEM,
  tempoLimiteMontagemMs = TEMPO_LIMITE_MONTAGEM_MS,
  prazoEnvioMs = PRAZO_ENVIO_MS,
  tempoMaximoTentativaEnvioMs = TEMPO_MAXIMO_TENTATIVA_ENVIO_MS,
  esperasEnvio = ESPERAS_ENVIO_MS,
  agora = () => Date.now(),
  esperar = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  // Tenta trazer de volta a foto do canvas `index` depois de uma exportação
  // que saiu branca (ver utils/restaurarCanvas.js).
  recuperarCanvas = async () => {},
}) {
  let listaPreviewsFinais = [];
  let erroPreviewFinal = "";
  let loadingFinalizar = false;
  let contadorUpload = 0;
  let contadorUploadMax = 0;

  function rotateFinalImage(imageDataUrl) {
    const degrees = 90;
    return new Promise((resolve) => {
      const canvasEl = document.createElement("canvas");
      const ctx = canvasEl.getContext("2d");
      const image = new Image();

      image.onload = function () {
        canvasEl.width = degrees % 180 === 0 ? image.width : image.height;
        canvasEl.height = degrees % 180 === 0 ? image.height : image.width;
        ctx.translate(canvasEl.width / 2, canvasEl.height / 2);
        ctx.rotate((degrees * Math.PI) / 180);
        ctx.drawImage(image, image.width / -2, image.height / -2);
        resolve(canvasEl.toDataURL());
      };

      image.onerror = function () {
        resolve(imageDataUrl);
      };

      image.src = imageDataUrl;
    });
  }

  function analyzeImageDataUrl(dataUrl) {
    return analyzeImageDataUrlUtil(dataUrl);
  }

  function getCanvasExportSignature(index) {
    const canvasInst = canvas[index].instancia;
    return JSON.stringify(canvasInst.toJSON());
  }

  async function exportCanvasWithValidation(index, maxAttempts = 3) {
    const canvasInst = canvas[index].instancia;
    const multiplier = formato.scaleFinal;
    const options = { format: "png", quality: 1, multiplier };
    return exportCanvasWithValidationUtil({
      canvasInst,
      tipo: formato.tipo,
      changedOrientation: changedOrientationFn(index),
      typeIsOrientation: typeIsOrientationFn(),
      rotateFinalImage,
      maxAttempts,
      options,
      recuperar: (tentativa) => recuperarCanvas(index, tentativa),
    });
  }

  function hasPreviewSyncWithCanvas(previews) {
    if (!previews || previews.length !== canvas.length) return false;
    return previews.every((preview) => {
      if (!preview || typeof preview.index !== "number") return false;
      const assinaturaAtual = getCanvasExportSignature(preview.index);
      return preview.assinatura === assinaturaAtual;
    });
  }

  // Tamanho da folha de impressão unificado: 15,02 × 10,01 cm a 300 DPI
  // 15,02 cm / 2,54 × 300 = 1774 px  |  10,01 cm / 2,54 × 300 = 1182 px
  const PRINT_SHEET_W = 1774;
  const PRINT_SHEET_H = 1182;

  // Dimensão real do canvas polaroid exportado (após scaleFinal × previewScale)
  const POLAROID_PHOTO_W = 1200;
  const POLAROID_PHOTO_H = 1600;

  // Espessura da linha de corte preta desenhada ao redor de cada foto 3x4, em mm
  const CUT_LINE_3X4_MM = 0.5;
  // Fator de correção da altura da foto 3x4.
  // A impressora estica ~4% na vertical, imprimindo ~4,1–4,2cm quando enviamos 4,0cm.
  // Reduzimos a altura desenhada para o resultado físico ficar em 4,0cm.
  // Ajuste conforme a medição real: fator = 4.0 / altura_medida_em_cm.
  const ALTURA_3X4_CORRECAO = 4.0 / 4.15;
  // O esticamento vertical é da impressora, não do 3x4: o passaporte usa o
  // mesmo fator (o lado de 5cm saiu com 5,00cm na impressão de teste).
  const ALTURA_PASSAPORTE_CORRECAO = ALTURA_3X4_CORRECAO;
  // Na horizontal a impressora também estica um pouco: a foto passaporte
  // enviada com 7,00cm (827px) saiu com 7,02cm. Fator = 7.0 / 7.02 (o lado
  // maior da foto fica na horizontal da folha). Precisão de 300 DPI: 1px ≈
  // 0,0085cm.
  const LARGURA_PASSAPORTE_CORRECAO = 7.0 / 7.02;
  // Linha de corte do passaporte no mesmo padrão da 3x4: contínua, #CCCCCC,
  // 0,5mm. Fica por fora de cada foto (a foto é desenhada por cima da linha,
  // então a linha nunca cobre a foto). Onde o vão entre duas fotos é menor
  // que duas linhas, as linhas das duas fotos se juntam numa só, como na 3x4.
  const COR_LINHA_CORTE_PASSAPORTE = "#CCCCCC";

  /**
   * Encaixa uma foto (qualquer tamanho) em uma folha, mantendo
   * a proporção e centralizando sobre fundo branco.
   * Rejeita (ErroComposicaoFolha) se a foto não entrar na folha.
   */
  async function composeSheet(sheetW, sheetH, dataUrl, analiseEsperada) {
    const img = await carregarImagemDaFolha(dataUrl, 1);
    const { sheetCanvas, ctx } = criarFolha(sheetW, sheetH);

    const scale = Math.min(sheetW / img.width, sheetH / img.height);
    let w = Math.round(img.width * scale);
    let h = Math.round(img.height * scale);
    // Expande 1 px quando arredondamento deixa uma fresta de 1 px numa borda
    if (sheetW - w === 1) w = sheetW;
    if (sheetH - h === 1) h = sheetH;
    const x = Math.round((sheetW - w) / 2);
    const y = Math.round((sheetH - h) / 2);
    ctx.drawImage(img, x, y, w, h);

    conferirRegiao(sheetCanvas, { x, y, w, h }, analiseEsperada, 1);
    return exportarFolha(sheetCanvas);
  }

  /**
   * Compõe 1 ou 2 fotos polaroid (1200×1600) lado a lado
   * em uma folha 1774×1182 (15,02×10,01 cm a 300 DPI).
   * Slot por foto: 887×1182 — encaixa a polaroid em escala 0,739.
   * Se qualquer uma das fotos falhar, a folha inteira falha: meia folha em
   * branco também é foto perdida.
   */
  async function composePolaroidSheet(
    dataUrl1,
    dataUrl2,
    analisesEsperadas = []
  ) {
    const sources = [dataUrl1, dataUrl2].filter(Boolean);
    const imgs = await Promise.all(
      sources.map((src, i) => carregarImagemDaFolha(src, i + 1))
    );
    const { sheetCanvas, ctx } = criarFolha(PRINT_SHEET_W, PRINT_SHEET_H);

    const slotW = Math.floor(PRINT_SHEET_W / 2); // 887
    const slotH = PRINT_SHEET_H; // 1182
    const scale = Math.min(slotW / POLAROID_PHOTO_W, slotH / POLAROID_PHOTO_H);
    const photoW = Math.round(POLAROID_PHOTO_W * scale);
    const photoH = Math.round(POLAROID_PHOTO_H * scale);

    imgs.forEach((el, idx) => {
      const x = idx * slotW + Math.round((slotW - photoW) / 2);
      const y = Math.round((slotH - photoH) / 2);
      ctx.drawImage(el, x, y, photoW, photoH);
      conferirRegiao(
        sheetCanvas,
        { x, y, w: photoW, h: photoH },
        analisesEsperadas[idx],
        idx + 1
      );
    });

    return exportarFolha(sheetCanvas);
  }

  /**
   * Compõe 8 cópias da mesma foto (3cm×4cm) em grade 4×2 em folha paisagem 1774×1182.
   * Slot: 354px de largura (3cm) × altura corrigida (ALTURA_3X4_CORRECAO) a 300 DPI.
   * Grupo centrado na folha, com linhas de corte pretas ao redor de cada foto.
   */
  async function compose3x4Sheet(dataUrl, analiseEsperada) {
    const COLS = 4;
    const ROWS = 2;
    const slotW = Math.round((3 * 300) / 2.54); // 354px = 3cm a 300 DPI
    // Altura reduzida para compensar o esticamento vertical da impressora
    const slotH = Math.round(((4 * 300) / 2.54) * ALTURA_3X4_CORRECAO);
    // Espessura da linha de corte preta ao redor de cada foto
    const lineW = Math.max(1, Math.round((CUT_LINE_3X4_MM / 25.4) * 300)); // ~6px
    const sheetW = PRINT_SHEET_W; // 1774 (paisagem)
    const sheetH = PRINT_SHEET_H; // 1182 (paisagem)
    // Fotos adjacentes; as linhas de corte pretas são desenhadas sobre as bordas
    const gridW = COLS * slotW;
    const gridH = ROWS * slotH;
    // Margem para centralizar as 8 fotos na folha
    const marginX = Math.round((sheetW - gridW) / 2);
    const marginY = Math.round((sheetH - gridH) / 2);

    const img = await carregarImagemDaFolha(dataUrl, 1);
    const { sheetCanvas, ctx } = criarFolha(sheetW, sheetH);

    // A foto preenche o slot inteiro (até as linhas de corte). A leve
    // compressão vertical é revertida pelo esticamento da impressora,
    // resultando em 3×4cm reais sem sobras brancas nas laterais.
    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col < COLS; col++) {
        const slotX = marginX + col * slotW;
        const slotY = marginY + row * slotH;
        ctx.drawImage(img, slotX, slotY, slotW, slotH);
      }
    }

    // Antes das linhas de corte, que também pintariam a área conferida.
    conferirRegiao(
      sheetCanvas,
      { x: marginX, y: marginY, w: slotW, h: slotH },
      analiseEsperada,
      1
    );

    // Linhas de corte pretas ao redor de cada foto (grade + bordas externas)
    ctx.strokeStyle = "#CCCCCC";
    ctx.lineWidth = lineW;
    for (let col = 0; col <= COLS; col++) {
      const x = marginX + col * slotW;
      ctx.beginPath();
      ctx.moveTo(x, marginY);
      ctx.lineTo(x, marginY + gridH);
      ctx.stroke();
    }
    for (let row = 0; row <= ROWS; row++) {
      const y = marginY + row * slotH;
      ctx.beginPath();
      ctx.moveTo(marginX, y);
      ctx.lineTo(marginX + gridW, y);
      ctx.stroke();
    }

    return exportarFolha(sheetCanvas);
  }

  // Exporta de novo as fotos da folha a partir do canvas do editor: o dataUrl
  // da prévia pode ser o problema.
  async function regenerarPreviews(previews) {
    const regenerados = [];
    for (const preview of previews) {
      const resultado = await exportCanvasWithValidation(preview.index);
      regenerados.push(
        resultado && resultado.imagemDataUrl
          ? {
              ...preview,
              imagemDataUrl: resultado.imagemDataUrl,
              ultimaAnalise: resultado.ultimaAnalise,
            }
          : preview
      );
    }
    return regenerados;
  }

  // Uma tentativa de montagem: com tempo máximo, e qualquer erro inesperado
  // (inclusive de dentro do desenho) vira ErroComposicaoFolha.
  async function tentarMontar(compor, fontes) {
    try {
      return await comTimeout(
        Promise.resolve().then(() => compor(fontes)),
        {
          ms: tempoLimiteMontagemMs,
          rotulo: "montagem da folha",
        }
      );
    } catch (erro) {
      if (erro instanceof ErroComposicaoFolha) throw erro;
      if (erro && erro.isTimeout) {
        throw new ErroComposicaoFolha("Montagem da folha passou do tempo", {
          tipo: FALHAS_FOLHA.TEMPO_ESGOTADO,
          causa: erro.message,
        });
      }
      throw new ErroComposicaoFolha("Erro inesperado ao montar a folha", {
        tipo: FALHAS_FOLHA.MONTAGEM,
        causa: (erro && erro.message) || String(erro),
      });
    }
  }

  // Monta uma folha com novas tentativas. Esgotadas, lança ErroComposicaoFolha
  // com o tipo da última falha e as causas de cada tentativa.
  async function montarFolha(previewsDaFolha, compor) {
    const causas = [];
    let ultimoErro = null;
    for (let tentativa = 1; tentativa <= tentativasMontagem; tentativa++) {
      try {
        const fontes =
          tentativa === tentativasMontagem && tentativa > 1
            ? await regenerarPreviews(previewsDaFolha)
            : previewsDaFolha;
        return await tentarMontar(compor, fontes);
      } catch (erro) {
        ultimoErro = erro;
        causas.push(`${erro.tipo || "erro"}:${erro.detalhes?.causa || ""}`);
        console.warn("Falha ao montar folha de impressão", {
          fotos: previewsDaFolha.map((p) => p.index),
          tentativa,
          erro: erro.message,
        });
      }
    }
    throw new ErroComposicaoFolha(ultimoErro.message, {
      tipo: ultimoErro.tipo,
      causa: ultimoErro.detalhes?.causa,
      indices: previewsDaFolha.map((p) => p.index),
      tentativas: tentativasMontagem,
      causas,
    });
  }

  // Envia uma folha tentando de novo até acabar o prazo (próprio de cada
  // folha). Cada tentativa tem limite próprio (no SDK e aqui, porque o limite
  // do SDK não derruba uma requisição já pendurada). Esgotado o prazo, lança
  // ErroEnvioFolha.
  async function enviarFolha(urlRef, dataUrl, indices) {
    const inicio = agora();
    const restante = () => prazoEnvioMs - (agora() - inicio);
    const causas = [];
    let tentativa = 0;
    const desistir = () =>
      new ErroEnvioFolha("Falha ao enviar a folha", {
        indices,
        tentativas: tentativa,
        causa: causas[causas.length - 1] || "prazo_esgotado",
        causas: causas.slice(-10),
      });
    for (;;) {
      tentativa += 1;
      const limite = Math.min(tempoMaximoTentativaEnvioMs, restante());
      try {
        await comTimeout(
          Promise.resolve().then(() =>
            uploadImageDataURL(urlRef, dataUrl, { tempoMaximoMs: limite })
          ),
          { ms: limite, rotulo: "envio da folha" }
        );
        return;
      } catch (erro) {
        causas.push((erro && (erro.code || erro.message)) || String(erro));
      }
      const espera =
        esperasEnvio[Math.min(tentativa - 1, esperasEnvio.length - 1)];
      // Sem tempo para esperar e tentar de novo dentro do prazo: desiste.
      if (restante() <= espera) throw desistir();
      await esperar(espera);
    }
  }

  // Monta e envia as folhas uma a uma. A etapa só avança depois do último
  // envio: uma falha no meio não libera pagamento nem impressão.
  async function montarEEnviarFolhas(grupos, compor, uploadMax, onUploadDone) {
    let localUpload = 0;
    for (const grupo of grupos) {
      const dataUrl = await montarFolha(grupo, compor);
      const urlRef = canvas[grupo[0].index].propriedades.url_editada;
      await enviarFolha(
        urlRef,
        dataUrl,
        grupo.map((p) => p.index)
      );
      localUpload += 1;
      if (onUploadDone) onUploadDone(localUpload, uploadMax);
      if (uploadMax > 0 && uploadMax === localUpload) {
        await onExportCompleted();
        await setEtapaCompra(typeEtapa.PAGAMENTO_SELECT);
      }
    }
  }

  /**
   * Agrupa os previews polaroid em pares, compõe cada par em uma folha
   * 1774×1182 e faz upload. Chama setEtapaCompra ao finalizar tudo.
   */
  async function savePolaroidSheets(previews, uploadMax, onUploadDone) {
    const pares = [];
    for (let i = 0; i < previews.length; i += 2) {
      pares.push([previews[i], previews[i + 1]].filter(Boolean));
    }
    await montarEEnviarFolhas(
      pares,
      ([p1, p2]) =>
        composePolaroidSheet(p1.imagemDataUrl, p2 ? p2.imagemDataUrl : null, [
          p1.ultimaAnalise,
          p2 && p2.ultimaAnalise,
        ]),
      uploadMax,
      onUploadDone
    );
  }

  /**
   * Para cada preview 3x4, compõe 8 cópias em uma folha paisagem 1774×1182
   * e faz upload. Chama setEtapaCompra ao finalizar.
   */
  async function save3x4Sheets(previews, uploadMax, onUploadDone) {
    await montarEEnviarFolhas(
      previews.map((preview) => [preview]),
      ([p]) => compose3x4Sheet(p.imagemDataUrl, p.ultimaAnalise),
      uploadMax,
      onUploadDone
    );
  }

  /**
   * Compõe 4 cópias da mesma foto passaporte (5,1cm×7,1cm) em grade 2×2 na
   * folha paisagem 1774×1182. Na folha cada foto fica deitada (7,1 × 5,1cm): a
   * foto retrato é girada 90°; a de paisagem entra direto. Sem esticar a imagem.
   * A sobra da folha é dividida igualmente entre bordas e espaço entre fotos.
   */
  async function composePassaporteSheet(dataUrl, analiseEsperada) {
    const COLS = 2;
    const ROWS = 2;
    const pxPorCm = 300 / 2.54;
    // Lado de 7,1cm na horizontal, reduzido pelo esticamento horizontal da
    // impressora: 836px ≈ 7,096cm impressos
    const slotW = Math.round(
      PASSAPORTE_ALTURA_CM * pxPorCm * LARGURA_PASSAPORTE_CORRECAO
    );
    // Lado de 5,1cm na vertical, reduzido pelo esticamento vertical:
    // 581px ≈ 5,104cm impressos. Duas fileiras quase ocupam a folha toda;
    // sobra ~0,5mm para cada espaço/borda.
    const slotH = Math.round(
      PASSAPORTE_LARGURA_CM * pxPorCm * ALTURA_PASSAPORTE_CORRECAO
    );
    const sheetW = PRINT_SHEET_W;
    const sheetH = PRINT_SHEET_H;
    const espacoX = Math.floor((sheetW - COLS * slotW) / (COLS + 1));
    const espacoY = Math.floor((sheetH - ROWS * slotH) / (ROWS + 1));
    const marginX = Math.round(
      (sheetW - COLS * slotW - (COLS - 1) * espacoX) / 2
    );
    const marginY = Math.round(
      (sheetH - ROWS * slotH - (ROWS - 1) * espacoY) / 2
    );

    const img = await carregarImagemDaFolha(dataUrl, 1);
    const { sheetCanvas, ctx } = criarFolha(sheetW, sheetH);

    const retrato = img.height > img.width;
    const posicoes = [];
    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col < COLS; col++) {
        posicoes.push({
          slotX: marginX + col * (slotW + espacoX),
          slotY: marginY + row * (slotH + espacoY),
        });
      }
    }

    // Primeiro as linhas de corte, depois as fotos por cima: a linha de uma
    // foto nunca cobre a foto vizinha.
    desenharMolduraDeCortePassaporte(ctx, posicoes, {
      slotW,
      slotH,
      // Mesma espessura da linha de corte da 3x4 (~6px), sem passar da borda
      // da folha.
      espessura: Math.max(
        1,
        Math.min(Math.round((CUT_LINE_3X4_MM / 25.4) * 300), marginX, marginY)
      ),
    });

    posicoes.forEach(({ slotX, slotY }) => {
      if (retrato) {
        ctx.save();
        ctx.translate(slotX + slotW / 2, slotY + slotH / 2);
        ctx.rotate(-Math.PI / 2);
        ctx.drawImage(img, -slotH / 2, -slotW / 2, slotH, slotW);
        ctx.restore();
      } else {
        ctx.drawImage(img, slotX, slotY, slotW, slotH);
      }
    });

    conferirRegiao(
      sheetCanvas,
      { x: marginX, y: marginY, w: slotW, h: slotH },
      analiseEsperada,
      1
    );

    return exportarFolha(sheetCanvas);
  }

  // Moldura contínua de `espessura` px em volta de cada foto, por fora dela.
  function desenharMolduraDeCortePassaporte(
    ctx,
    posicoes,
    { slotW, slotH, espessura }
  ) {
    const larguraTotal = slotW + 2 * espessura;
    ctx.fillStyle = COR_LINHA_CORTE_PASSAPORTE;
    posicoes.forEach(({ slotX, slotY }) => {
      ctx.fillRect(
        slotX - espessura,
        slotY - espessura,
        larguraTotal,
        espessura
      );
      ctx.fillRect(slotX - espessura, slotY + slotH, larguraTotal, espessura);
      ctx.fillRect(slotX - espessura, slotY, espessura, slotH);
      ctx.fillRect(slotX + slotW, slotY, espessura, slotH);
    });
  }

  /**
   * Para cada preview passaporte, compõe 4 cópias em uma folha 1774×1182
   * e faz upload. Chama setEtapaCompra ao finalizar.
   */
  async function savePassaporteSheets(previews, uploadMax, onUploadDone) {
    await montarEEnviarFolhas(
      previews.map((preview) => [preview]),
      ([p]) => composePassaporteSheet(p.imagemDataUrl, p.ultimaAnalise),
      uploadMax,
      onUploadDone
    );
  }

  /**
   * Para cada preview normal, compõe a foto em uma folha 1774×1182
   * (15,02×10,01 cm a 300 DPI) e faz upload. Chama setEtapaCompra ao finalizar.
   */
  async function saveNormalSheets(previews, uploadMax, onUploadDone) {
    await montarEEnviarFolhas(
      previews.map((preview) => [preview]),
      ([p]) =>
        composeSheet(
          PRINT_SHEET_W,
          PRINT_SHEET_H,
          p.imagemDataUrl,
          p.ultimaAnalise
        ),
      uploadMax,
      onUploadDone
    );
  }

  async function salvaCanvas(
    canvasItem,
    index,
    onUploadDone,
    previews,
    uploadMax
  ) {
    const previewExistente = (previews || []).find(
      (item) => item.index === index
    );
    if (!previewExistente || !previewExistente.imagemDataUrl) {
      throw new Error(`Preview final ausente para o indice ${index}`);
    }
    const urlRefEditada = canvas[index].propriedades.url_editada;
    await enviarFolha(urlRefEditada, previewExistente.imagemDataUrl, [index]);
    contadorUpload += 1;
    const max = uploadMax != null ? uploadMax : contadorUploadMax;
    if (onUploadDone) onUploadDone(contadorUpload, max);
    if (max > 0 && max == contadorUpload) {
      loadingFinalizar = false;
      await onExportCompleted();
      await setEtapaCompra(typeEtapa.PAGAMENTO_SELECT);
    }
  }

  return {
    listaPreviewsFinais,
    erroPreviewFinal,
    loadingFinalizar,
    contadorUpload,
    contadorUploadMax,
    rotateFinalImage,
    analyzeImageDataUrl,
    getCanvasExportSignature,
    exportCanvasWithValidation,
    hasPreviewSyncWithCanvas,
    composeSheet,
    composePolaroidSheet,
    compose3x4Sheet,
    salvaCanvas,
    savePolaroidSheets,
    saveNormalSheets,
    save3x4Sheets,
    composePassaporteSheet,
    savePassaporteSheets,
  };
}
